import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { StatusKind } from '@erp/shared';
import { formatDateTime } from '@/lib/utils';
import { Badge, EmptyState, Skeleton } from '@/components/ui/misc';
import { clientTicketsOptions, errorMessage } from '@/features/clients/clients-queries';

const KIND_VARIANT: Record<StatusKind, 'info' | 'success' | 'destructive' | 'default'> = {
  open: 'info',
  closed: 'success',
  canceled: 'destructive',
  custom: 'default',
};

export function ClientTicketsSection({ clientId }: { clientId: number }) {
  const tickets = useQuery(clientTicketsOptions(clientId));

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="flex items-center justify-between gap-2 p-4 pb-3">
        <h2 className="text-base font-semibold">Tickets do cliente</h2>
        {tickets.data ? (
          <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground tabular-nums">
            {tickets.data.total}
          </span>
        ) : null}
      </div>
      {tickets.isPending ? (
        <div aria-busy className="space-y-3 px-4 pb-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-16 rounded-sm" />
            </div>
          ))}
        </div>
      ) : tickets.isError ? (
        <p className="px-4 pb-4 text-sm text-destructive">
          Erro ao carregar tickets: {errorMessage(tickets.error)}
        </p>
      ) : tickets.data.items.length === 0 ? (
        <EmptyState className="h-32" message="Nenhum ticket para este cliente" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Número</th>
                <th className="h-9 px-4 font-semibold">Título</th>
                <th className="h-9 px-4 font-semibold">Mesa</th>
                <th className="h-9 px-4 font-semibold">Estágio</th>
                <th className="h-9 px-4 font-semibold">Status</th>
                <th className="h-9 px-4 font-semibold">Atualizado</th>
              </tr>
            </thead>
            <tbody>
              {tickets.data.items.map((t) => (
                <tr
                  key={t.ticketNumber}
                  className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                >
                  <td className="px-4 whitespace-nowrap">
                    <Link
                      to="/tickets/$ticketNumber"
                      params={{ ticketNumber: String(t.ticketNumber) }}
                      className="font-medium text-primary tabular-nums hover:text-primary-pressed hover:underline"
                    >
                      #{t.ticketNumber}
                    </Link>
                  </td>
                  <td className="max-w-md truncate px-4" title={t.title}>
                    {t.title}
                  </td>
                  <td className="max-w-40 truncate px-4 text-muted-foreground">{t.desk.name}</td>
                  <td className="max-w-40 truncate px-4 text-muted-foreground">{t.stage.name}</td>
                  <td className="px-4 whitespace-nowrap">
                    <Badge variant={KIND_VARIANT[t.status.kind]}>{t.status.name}</Badge>
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatDateTime(t.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
